import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import worriesSectionImage from "url:src/assets/images/top/worries-section.png";
import resolveSectionImage from "url:src/assets/images/top/resolve-section.png";
import formBackgroundImage from "url:src/assets/images/top/form-background.jpg";
import reasonSectionImage from "url:src/assets/images/top/reason-section.png";
import sellFlowSectionImage from "url:src/assets/images/top/sell-flow-section.png";
import fmissionSectionImage from "url:src/assets/images/top/mission-section.png";
import Section from "src/components/Section";
import SimpleFormSection from "./components/SimpleFormSection";
import { SimpleForm } from "./components/SimpleForm";
import CaseSection from "../../components/CaseSection";
import FAQSection from "../../components/FAQSection";
import { useGetCities } from "src/hooks/address";
import { Check } from "@mui/icons-material";
import { useTopPage } from "./hooks";
import { fvExperimentABTester, RichSpan, RichTextSpan } from "src/utils/fvExperimentABTester";

export default function TopPage() {
  useTopPage();
  const { cities } = useGetCities();

  const expRef = React.useRef<fvExperimentABTester | null>(null);
  if (!expRef.current) {
    expRef.current = new fvExperimentABTester({
      experimentId: "lp_v2_fv7_variants_v1",
      storageKey: "exp_lp_v2_fv7",
    });
  }
  const exp = expRef.current;

  // FVの種類は初回のみ決定
  const [variant] = React.useState(() => exp.getVariant());

  React.useEffect(() => {
    exp.trackView(variant);
  }, [exp, variant]);

  const cfg = exp.getConfig(variant);
  const headline = cfg.headline;
  const bullets = cfg.bullets;

  const fvBgImage = cfg.bg?.image ? `url(${cfg.bg.image})` : `url(${formBackgroundImage})`;
  const isTextSpan = (s: RichSpan): s is RichTextSpan => "text" in s;

  // 共通 FV スタック
  const FvInner = (
    <Stack spacing={cfg.ctaGap ?? 4} sx={{ position: "relative", zIndex: 2, width: "100%", p: "40px 20px" }}>
      {/* 見出し */}
      <Box component="h1" sx={{ m: 0 }}>
        {fvExperimentABTester.renderRichBlock(headline)}
      </Box>

      {/* CTA 上配置パターン */}
      {cfg.cta.placement === "above-bullets" && (
        <Stack>
          <SimpleForm cities={cities} ctaClassName={`${cfg.cta.classSuffix} ${variant} cta-fv`} />
        </Stack>
      )}

      {/* bullets */}
      <Stack spacing={1} sx={{ textAlign: "left", width: "100%" }}>
        {bullets.map((line, i) => (
          <Stack key={`bullet-${i}`} direction="row" alignItems="center" spacing={1}>
            <Check sx={{ color: "white", ...(cfg?.bulletsSx || {}) }} />
            <Typography>
              {line.map(
                (s, j) =>
                  isTextSpan(s) ? (
                    <Typography key={`bullet-${i}-${j}`} component="span" sx={s.sx}>
                      {s.text}
                    </Typography>
                  ) : null, // 画像スパンが来た場合は無視（必要なら別描画を）
              )}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* CTA 下配置パターン */}
      {cfg.cta.placement === "below-bullets" && (
        <Stack>
          <SimpleForm cities={cities} ctaClassName={`${cfg.cta.classSuffix} ${variant} cta-fv`} />
        </Stack>
      )}
    </Stack>
  );

  return (
    <>
      {/* メインビジュアル */}
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          // 画像 or 既存デフォルト
          backgroundImage: fvBgImage,
          backgroundSize: "cover",
          position: "relative",
          // バリアント個別の FV 高さ・背景Sx を上書き
          height: cfg.containerSx?.height ?? "520px",
          ...(cfg.bg?.sx ?? {}),
        }}
      >
        {FvInner}
      </Stack>

      {/* こんなお悩みありませんかセクション */}
      <Box
        id="features-section"
        component="img"
        src={worriesSectionImage}
        alt="こんなお悩みありませんか"
        sx={{ width: "100%", display: "block", scrollMarginTop: "30px" }}
      />

      {/* その悩み、PropallyのAI査定なら解決できます！セクション */}
      <Box
        component="img"
        src={resolveSectionImage}
        alt="その悩み、PropallyのAI査定なら解決できます！"
        sx={{ width: "100%", display: "block" }}
      />

      {/* Propallyが選ばれる3つの理由セクション */}
      <Box
        component="img"
        src={reasonSectionImage}
        alt="Propallyが選ばれる3つの理由"
        sx={{ width: "100%", display: "block" }}
      />

      {/* 査定フォームセクション */}
      <SimpleFormSection cities={cities} ctaClassName={`cta-reason ${variant}`} />

      {/* 成約事例セクション */}
      <Section title="成約事例" id="case-section" sx={{ scrollMarginTop: "30px" }}>
        <CaseSection />
      </Section>

      {/* 売却の流れセクション */}
      <Box
        component="img"
        src={sellFlowSectionImage}
        alt="売却の流れ"
        sx={{ width: "100%", display: "block", scrollMarginTop: "30px" }}
        id="sell-flow-section"
      />

      {/* 査定フォームセクション */}
      <SimpleFormSection cities={cities} ctaClassName={`cta-sell ${variant}`} />

      {/* よくある質問セクション */}
      <Section title="よくある質問" id="faq-section" sx={{ scrollMarginTop: "30px" }}>
        <FAQSection />
      </Section>

      {/* 査定フォームセクション */}
      <SimpleFormSection cities={cities} ctaClassName={`cta-faq ${variant}`} />

      {/* ミッションセクション */}
      <Box
        component="img"
        src={fmissionSectionImage}
        alt="不動産投資をもっとクリアに"
        sx={{ width: "100%", display: "block" }}
      />
    </>
  );
}
